import { alpha, Button, Card, Collapse, Slider, Stack, Theme, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { VoiceSettings } from '../../../types/messageUsersAutoSendWelcomeMessage';

const ElevenLabsVoiceSettingsEditor = (props: {
    voiceSettings: VoiceSettings | null;
    onChange: (newVoiceSettings: VoiceSettings | null) => void;
    disabled?: boolean;
}) => {
    const { voiceSettings, onChange, disabled } = props;

    const theme: Theme = useTheme();
    const [open, setOpen] = useState(false);

    return (
        <Stack direction="column" spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">Advanced Voice Settings</Typography>
                <Button variant="text" color="primary" onClick={() => setOpen(!open)}>
                    {open ? 'Hide' : 'Show'}
                </Button>
            </Stack>

            <Collapse in={open} unmountOnExit={true}>
                <Card
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: alpha(theme.palette.primary.light, 0.05),
                        borderRadius: theme.shape.borderRadius,
                        borderColor: theme.palette.primary.main,
                    }}
                >
                    <Stack direction="column" spacing={0}>
                        <Typography variant="body2">Stability</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Determines how stable the voice is and the randomness between each generation. Lower values introduce broader
                            emotional range for the voice. Higher values can result in a monotonous voice with limited emotion.
                        </Typography>
                    </Stack>
                    {/* stability slider (default: 0.5) */}
                    <Slider
                        value={voiceSettings?.stability !== undefined ? voiceSettings.stability : 0.5}
                        onChange={(e, newValue) => {
                            const stability = newValue as number;
                            onChange({
                                ...(voiceSettings || {}),
                                stability: stability,
                            });
                        }}
                        valueLabelDisplay="auto"
                        step={0.1}
                        min={0}
                        max={1}
                        marks={[{ value: 0.5, label: '0.5' }]}
                        disabled={disabled}
                    />
                    {/* similarity boost slider (default: 0.75) */}
                    <Stack direction="column" spacing={0}>
                        <Typography variant="body2">Similarity Boost</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Determines how closely the AI should adhere to the original voice when attempting to replicate it.
                        </Typography>
                    </Stack>
                    <Slider
                        value={voiceSettings?.similarityBoost !== undefined ? voiceSettings.similarityBoost : 0.75}
                        onChange={(e, newValue) => {
                            const similarityBoost = newValue as number;
                            onChange({
                                ...(voiceSettings || {}),
                                similarityBoost: similarityBoost,
                            });
                        }}
                        valueLabelDisplay="auto"
                        step={0.05}
                        min={0}
                        max={1}
                        marks={[{ value: 0.75, label: '0.75' }]}
                        disabled={disabled}
                    />
                    {/* speed slider (default: 1.0) */}
                    <Stack direction="column" spacing={0}>
                        <Typography variant="body2">Speed</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Adjusts the speed of the voice. A value of 1.0 is the default speed, while values less than 1.0 slow down the
                            speech, and values greater than 1.0 speed it up
                        </Typography>
                    </Stack>
                    <Slider
                        value={voiceSettings?.speed !== undefined ? voiceSettings.speed : 1}
                        onChange={(e, newValue) => {
                            const speed = newValue as number;
                            onChange({
                                ...(voiceSettings || {}),
                                speed: speed,
                            });
                        }}
                        valueLabelDisplay="auto"
                        step={0.1}
                        min={0.5}
                        max={1.5}
                        marks={[{ value: 1, label: '1.0x' }]}
                        disabled={disabled}
                        valueLabelFormat={value => `${value.toFixed(1)}x`}
                    />
                </Card>
            </Collapse>
        </Stack>
    );
};

export default ElevenLabsVoiceSettingsEditor;
